import { useEffect, useRef } from "react";
import moment from "moment";
import { evalActions } from "../../actions/entities/eval.actions";
import { useAppDispatch } from "../../effects/useAppDispatch";
import { useAppSelector } from "../../effects/useAppSelector";
import { EvalOrderByColumn } from "../../types/state/entities/EvalState";
import { isRequesting, isRequestNone, isRequestSuccess } from "../../utils/request-state.utils";
import { Table } from "../bidding/common/table";
import { EmptyPlaceholder, Preloader } from "../common";
import { Relative } from "../common/Relative";
import { SecuritiesSearchInput } from "../filters/SecuritiesSearchInput";
import { EvalFilter } from "./EvalFilter";
import { OrderByDirection } from "../../types/OrderByDirection";
import { IColumnDefinition } from "../bidding/common/table/types/ColumnDefinition";
import { EvalDataItem } from "../../types/eval/EvalDataItem";
import { useManagersFilterData } from "../filters/dinamicFilterEffects/useManagersFilterData";
import { BwicFilterType } from "../../types/filters/FilterState";
import { ColumnBuilder } from "../bidding/common/table/columns/column-builder/ColumnBuilder";
import { constants } from "../../constants/constants";
import { PipelineColumn } from "../amrPipeline/types/PipelineColumn";
import { formatUtils, numericUtils } from "../../utils";
import { InfoTooltip } from "../common/InfoTooltip";
import { getAmrColumns } from "../amrPipeline/common-columns";
import { IntersectionObserverProvider } from "../common/IntersectionObsereverProvider";
import { EvalSubscriptionBanner } from "./EvalSubscriptionBanner";
import { EvalExportButton } from "./EvalExportButton";
import { createFilterActions } from "../../actions/filter.actions";
import { trackingService } from "../../services/action-tracking.service";
import { FeatureButton } from "../access/FeatureButton";
import { EvalActionBlocker } from "../access/EvalActionBlocker";
import { SubscriptionFeature } from "../../types/billing/SubscriptionFeature";
import { EvalValueBlocker } from "../access/EvalValueBlocker";
import { useHolidays } from "../../effects/useHolidays";
import { EvalTrialDataBlocker } from "../access/EvalTrialDataBlocker";
import IconSVG from "../../styles/svg-icons";
import { SecurityIdentifier } from "../security-details/SecurityIdentifier";
import { SORT } from "../../constants";

const filterActions = createFilterActions(BwicFilterType.Eval);

export function EvalView() {
    const dispatch = useAppDispatch();
    const requestState = useAppSelector(s => s.entities.eval.requestState)
    const evalDataItems = useAppSelector(s => s.entities.eval.items);
    const totalRecordNumber = useAppSelector(s => s.entities.eval.totalRecordNumber);

    const sortingField = useRef(EvalOrderByColumn.EvalDate);
    const sortDirection = useRef(SORT.DESC);

    const managersRequestState = useManagersFilterData(BwicFilterType.Eval);
    const { holidays, requestState: holidaysRequestState } = useHolidays();

    const isLoading =
        isRequestNone(managersRequestState, requestState, holidaysRequestState) ||
        isRequesting(managersRequestState, requestState, holidaysRequestState);

    useEffect(() => {
        if (isRequestSuccess(managersRequestState)) {
            dispatch(evalActions.init(sortingField.current, sortDirection.current));
        }
    }, [dispatch, managersRequestState]);

    const handleSearch = () => {
        dispatch(evalActions.reset());
        dispatch(evalActions.request(sortingField.current, sortDirection.current, true));
    }

    const handleNextPage = () => {
        if (!isRequesting(requestState)) {
            dispatch(evalActions.loadNextPage());
            dispatch(evalActions.request(sortingField.current, sortDirection.current, false));
        }
    };

    const handleReset = () => {
        dispatch(filterActions.resetFilter());
        dispatch(evalActions.request(sortingField.current, sortDirection.current, true));
    }

    const handleFeedbackClick = ({ ticker }: EvalDataItem) => {
        const mailto = `mailto:${constants.evalEmail}?subject=${encodeURIComponent('EVAL Trial Feedback')}`;
        window.open(mailto);

        trackingService.trackAction('Eval feedback click', { ticker });
    }

    function getColumns() {
        const {
            [PipelineColumn.manager]: collateralManager,
            [PipelineColumn.collateralType]: collateralType,
        } = getAmrColumns();

        const columns: IColumnDefinition<EvalDataItem>[] = [{
            columnKey: 'ticker',
            renderColumnHeaderContent: () => 'Ticker',
            renderColumnContent: x => x.ticker,
            className: 'data-list-cell-lg-02'
        }, {
            columnKey: 'isinCusip',
            renderColumnHeaderContent: () => 'Identifier',
            renderColumnContent: position =>
                <SecurityIdentifier
                    ticker={position.ticker}
                    identifiers={position.identifiers}
                    currency={position.currency}
                />,
            className: 'data-list-cell-sm'
        }, {
            columnKey: 'price',
            renderColumnHeaderContent: () =>
                <>
                    <IconSVG name="kopentech" width={16} height={16} className="margin-r-4" />
                    KTX Price
                    <InfoTooltip text="New prices are available 6 PM EST daily" />
                </>,
            renderColumnContent: x =>
                <EvalValueBlocker feature={SubscriptionFeature.CanViewEvalData}>
                    <EvalTrialDataBlocker evalDate={x.date} holidays={holidays}>
                        {
                            limited => limited
                                ? <IconSVG width="16" height="16" name="subscribe-lock-grey" />
                                : numericUtils.isNumber(x.price) ? formatUtils.formatDecimal(x.price, 4) : constants.emptyPlaceholder
                        }
                    </EvalTrialDataBlocker>
                </EvalValueBlocker>,
            className: "data-list-cell-md text-right"
        }, {
            columnKey: EvalOrderByColumn.EvalDate,
            renderColumnHeaderContent: () => 'EVAL Date',
            renderColumnContent: x => moment.utc(x.date).startOf('day').format(constants.dateFormat),
            sortingField: EvalOrderByColumn.EvalDate,
            className: "data-list-cell-sm-02"
        }, {
            columnKey: 'rating',
            renderColumnHeaderContent: () => 'Rtg',
            renderColumnContent: x => x.rating,
            className: "data-list-cell-xs"
        }, {
            ...collateralManager,
            sortingField: undefined,
            sortingType: undefined,
            configKey: undefined,
            className: "data-list-cell-xl"
        }, {
            columnKey: 'currency',
            renderColumnHeaderContent: () => 'Ccy',
            renderColumnContent: x => x.currency,
            className: 'data-list-cell-xs',
        }, {
            ...collateralType,
            sortingField: undefined,
            sortingType: undefined,
            configKey: undefined,
        }, {
            columnKey: 'actions',
            renderColumnHeaderContent: () => 'Actions',
            renderColumnContent: x =>
                <EvalActionBlocker feature={SubscriptionFeature.CanViewEvalData}>
                    <EvalTrialDataBlocker evalDate={x.date} holidays={holidays}>
                        {
                            limited => (
                                <FeatureButton
                                    disabled={limited}
                                    onClick={() => handleFeedbackClick(x)}
                                    className="btn btn-ghost btn-sm"
                                >
                                    Feedback
                                </FeatureButton>
                            )
                        }
                    </EvalTrialDataBlocker>
                </EvalActionBlocker>,
            className: 'data-list-cell-md-flexible data-list-cell-actions text-right'
        }];

        return columns.map(c => new ColumnBuilder(c));
    }

    return (
        <div className="container container-eval">
            <EvalSubscriptionBanner disabled={isLoading} />
            <div className="sub-header sub-header-pipeline">
                <div className="sub-header-row type01 flex-row">
                    <h1>EVAL</h1>
                    <Relative>
                        <SecuritiesSearchInput
                            disabled={isLoading}
                            onSearch={handleSearch}
                        />
                    </Relative>
                    <div className="flex-item-right">
                        <EvalExportButton
                            disabled={isLoading || !evalDataItems.length}
                            holidays={holidays}
                            sortingField={sortingField.current}
                            sortDirection={sortDirection.current}
                        />
                    </div>
                </div>
                <div className="sub-header-row type02 sub-header-filters row-align-filters">
                    <EvalFilter handleApplyFilter={handleSearch} />
                </div>
            </div>
            <div className="flex-row flex-row-full-size position-relative">
                <Preloader inProgress={(isLoading && !evalDataItems.length)}>
                    {evalDataItems.length ?
                        <IntersectionObserverProvider
                            disabled={totalRecordNumber <= 50}
                            observeTableRows={true}
                            options={{
                                rootMargin: "0% 100% 0% 100%" // ignore horizontal scrolling
                            }}
                        >
                            <Table
                                columns={getColumns()}
                                dataItems={evalDataItems}
                                onNextPageRequest={handleNextPage}
                                infiniteScrollEnabled={evalDataItems.length < totalRecordNumber}
                                onSort={(field: string, direction: string) => {
                                    sortingField.current = field as EvalOrderByColumn;
                                    sortDirection.current = direction as OrderByDirection;
                                    handleSearch();
                                }}
                                defaultSortBy={sortingField.current}
                                defaultSortByDirection={sortDirection.current}
                                sticky={false}
                                isRightColumnCollapsible={false}
                                rightColumnCollapsedDefault={false}
                                className="data-list-striped"
                            />
                        </IntersectionObserverProvider>
                        : <EmptyPlaceholder text={
                            <>
                                <span className="placeholder-text">
                                    <button className="btn-link btn-link-lg regular secondary" onClick={handleReset}>Click here</button> to clear the filters/search for more results. Note: KTX Prices available from May 5, 2025.
                                </span>
                            </>
                        } />}
                </Preloader>
            </div>
        </div>
    )
}